import angular from 'angular';
import moment from 'moment';
import receiptsEntityInfoDialogTemplate from './ReceiptsEntityInfoDialog.html';
import CSMCommonWeb from 'csm-common-web';
import './ReceiptsEntityInfoDialog.scss';
import booksService from '../../booksServices';

// Child components used inside this dialog's tabs
import infoActions from './infoActions';
import individualInfo from './individualInfo';

/*
 * Dialog containing the address information, receivables and open orders for
 * the selected entity.
 */
export default angular
  .module('csm.books.com.ReceiptsEntityInfoDialog', [
    CSMCommonWeb,
    'ui.bootstrap',
    booksService,
    infoActions,
    individualInfo
  ])
  .value('ReceiptHistoryTab', null)
  .service('ReceiptsEntityInfoDialog', DialogControllerService)
  .component('receiptsEntityInfo', receiptsEntityInfoComponent()).name;

/*******************************************************************************
 * DialogControllerService */
/**
 * Dialog service that sets up the open orders and unpaid items handlers and the
 * show function.
 *******************************************************************************/
/* @ngInject */ function DialogControllerService(
  BusyScreen,
  $injector,
  BooksSvc,
  BooksServices,
  $uibModal
) {
  this.viewOpenOrdersHandler = $injector.invoke(viewOpenOrdersHandler, null, {
    BooksSvc,
    $ctrl: this
  });
  this.viewReceivablesItemsHandler = $injector.invoke(
    viewReceivablesItemsHandler,
    null,
    {
      BooksServices,
      $ctrl: this
    }
  );
  this.viewReceivablesItemsHandler = $injector.invoke(
    viewReceivablesItemsHandler,
    null,
    {
      BooksServices,
      $ctrl: this
    }
  );

  this.show = $injector.invoke(showDialog, null, {
    $uibModal,
    BusyScreen,
    $ctrl: this
  });
}

/*******************************************************************************
 * showDialog */
/**
 * Opens the entity info dialog. Resolves with an array of ReceivableItems to pay
 * down. Notifiers: creditMemo(receivable) is called with the receivable item
 * that was just credited; pickup() is called when a pickup receipt is created
 * that had a credit memo applied.
 *
 * @param  addressEntity  Entity information object, or null.
 * @param  barcode  Barcode string, or null.
 * @param  forceOpen  Forces the dialog open if it has no data.
 * @param  allowPayments  Receipts allow payments.
 * @param  showUnpaidItems  Show unpaid items (ASB true; Booster/PTA false).
 * @param  notifiers  Functions called on the parent when an event happens.
 * @param  stationInfo  Station information passed through to the dialog.
 *******************************************************************************/
/* @ngInject */ function showDialog(
  $ctrl,
  $q,
  $uibModal,
  BusyScreen,
  SERVER,
  Tools
) {
  return function (
    addressEntity,
    barcode,
    forceOpen,
    allowPayments,
    showUnpaidItems,
    notifiers,
    stationInfo
  ) {
    BusyScreen.setBusy(true);
    /**
     * If no entity is set, then we only show the open orders for the organization. We hide the address section
     * because there is no entity to get the address of. We hide the receivables section because receivables are tied
     * to entities and need to paid by the entity.
     */
    const entityExists = !!addressEntity;

    const orgInfo = !entityExists && !barcode;

    /**
     * We have to set these up on $ctrl that way the fns:
     * - viewOpenOrdersHandler
     * - viewReceivablesItemsHandler
     *
     * Are used by both the dialog controller and the main controller and expects its data to
     * exist on $ctrl.
     */
    $ctrl.orgInfo = orgInfo;
    $ctrl.barcode = barcode;
    $ctrl.addressEntity = addressEntity;

    /**
     * If the entity does not exist or the user signed in does not have the permission to view the entity address we hide the
     * address section.
     */
    const hideAddress = orgInfo || !!SERVER.appFlags.hideEntityAddress;

    /**
     * We always retrieve open orders but we pass in different filters based on what the user wants to see.
     *
     * 1. If the user input a receipt barcode they will see all open orders for that receipt.
     * 2. If the user selected an entity they will see all open orders for that entity.
     * 3. If no user or barcode was set then we show all open orders for the organization.
     */
    const openOrdersPromise = this.viewOpenOrdersHandler({
      barcode,
      entityID: entityExists ? addressEntity.entity.id : null,
      orgInfo
    });

    /**
     * We only show the receivables section if the user has the permission to view the receivables, the entity exists, and the application
     * allows receivables to be shown.
     *
     * We hide unpaid items for booster and pta.
     */
    const receivablesPromise = showUnpaidItems
      ? this.viewReceivablesItemsHandler({
          barcode,
          entityID: entityExists ? addressEntity.entity.id : null,
          orgInfo
        })
      : [];

    return $q.all([openOrdersPromise, receivablesPromise]).then((results) => {
      BusyScreen.setBusy(false);

      const openOrders = results[0];
      const receivables = results[1];

      // Only open the dialog if there is data to display, or if the dialog is being forced to open.
      const openDialog =
        forceOpen || !Tools.isEmpty(receivables) || !Tools.isEmpty(openOrders);

      if (!openDialog) {
        return $q.reject();
      }

      return $uibModal.open({
        animation: true,
        backdrop: 'static',
        component: 'receiptsEntityInfo',
        resolve: {
          addressEntity: () => addressEntity,
          allowReceivablesEdit: () => allowPayments,
          hideAddress: () => hideAddress,
          notifiers: () => {
            return angular.extend(
              {
                creditMemo: angular.noop,
                pickup: angular.noop
              },
              notifiers
            );
          },
          openOrders: () => openOrders,
          orgInfo: () => orgInfo,
          barcode: () => barcode,
          receivables: () => receivables,
          destinyStatus: () => $ctrl.destinyStatus,
          showUnpaidItems: () => showUnpaidItems,
          stationInfo: () => stationInfo
        },
        size: 'lg'
      }).result;
    });
  };
}

/*******************************************************************************
 * receiptsEntityInfoComponent */
/**
 * Component definition for the receipts entity info dialog.
 *******************************************************************************/
/* @ngInject */ function receiptsEntityInfoComponent() {
  return {
    template: receiptsEntityInfoDialogTemplate,
    bindings: { modalInstance: '<', resolve: '<' },
    controller: receiptsEntityInfoController
  };
}

/*******************************************************************************
 * receiptsEntityInfoController */
/**
 * Controller for the receipts entity info component.
 *******************************************************************************/
/* @ngInject */ function receiptsEntityInfoController($injector) {
  const $ctrl = this;
  const $svcs = { $ctrl };

  // Initialize component data
  $ctrl.$onInit = function () {
    $injector.invoke(initCtrl, null, Object.assign($svcs, { $svcs }));
  };
}

/*******************************************************************************
 * initCtrl */
/**
 * Builds the dialog's tab list from the entity data available and initializes
 * the controller.
 *******************************************************************************/
/* @ngInject */ function initCtrl($ctrl, $injector, $svcs, ReceiptHistoryTab) {
  const addressEntity = $ctrl.resolve.addressEntity;
  const hideAddress = $ctrl.resolve.hideAddress;

  /*--------------------------------------------------------------*/
  /* Build the tab list based on what data we actually have.      */
  /*                                                              */
  /*   - No addressEntity  -> no tabs at all (the modal body      */
  /*                          renders <info-actions> directly).   */
  /*   - addressEntity     -> show Actions, plus Individual Info  */
  /*                          when hideAddress is false, plus     */
  /*                          Receipt History when registered by  */
  /*                          the host app (ASBWorks only).       */
  /*--------------------------------------------------------------*/
  const tabs = !addressEntity
    ? []
    : [
        { id: 'actions', header: 'Actions', onSelect: angular.noop },
        ...(!hideAddress
          ? [
              {
                id: 'individualInfo',
                header: 'Individual Info',
                onSelect: angular.noop
              }
            ]
          : []),
        ...(ReceiptHistoryTab ? [ReceiptHistoryTab] : [])
      ];

  Object.assign($ctrl, {
    // Properties
    activeTab: 0,
    addressEntity,
    allowReceivablesEdit: $ctrl.resolve.allowReceivablesEdit,
    barcode: $ctrl.resolve.barcode,
    contentReady: true,
    hideAddress,
    notifiers: $ctrl.resolve.notifiers,
    openOrders: $ctrl.resolve.openOrders,
    orgInfo: $ctrl.resolve.orgInfo,
    receivables: $ctrl.resolve.receivables,
    destinyStatus: $ctrl.resolve.destinyStatus,
    stationInfo: $ctrl.resolve.stationInfo,
    showTabs: tabs.length > 0,
    showUnpaidItems: $ctrl.resolve.showUnpaidItems,
    /*-----------------------------------------------------------*/
    /* Tab metadata. The `id` drives ng-switch in the template   */
    /* so each tab body can render its own component with the    */
    /* bindings that component actually declares. `onSelect` is  */
    /* invoked by uib-tab when that tab is activated.            */
    /*-----------------------------------------------------------*/
    tabs,
    // Functions
    close: $ctrl.modalInstance.close,
    dismiss: $ctrl.modalInstance.dismiss,
    // Injector Functions
    dialogTitle: $injector.invoke(dialogTitle, null, $svcs),
    findEntityByDirection: $injector.invoke(findEntityByDirection, null, $svcs),
    viewOpenOrdersHandler: $injector.invoke(viewOpenOrdersHandler, null, $svcs),
    viewReceivablesItemsHandler: $injector.invoke(
      viewReceivablesItemsHandler,
      null,
      $svcs
    ),
    refreshDestinyFinesHandler: $injector.invoke(
      refreshDestinyFinesHandler,
      null,
      $svcs
    ),
    refreshDestinyEntityFinesHandler: $injector.invoke(
      refreshDestinyEntityFinesHandler,
      null,
      $svcs
    )
  });
}

/*******************************************************************************
 * dialogTitle */
/**
 * Formats the data to be displayed in the modal header.
 *******************************************************************************/
/* @ngInject */ function dialogTitle($ctrl, BarcodeValidator) {
  return function () {
    if ($ctrl.orgInfo) {
      return 'Organization Actions';
    }

    if ($ctrl.barcode && !$ctrl.addressEntity) {
      const receiptInfo = BarcodeValidator.parse($ctrl.barcode || '');

      /**
       * The receipt will be invalid if it has decimals or if it is in purchase order format.
       */
      const validReceipt =
        receiptInfo.isValid && (receiptInfo.isReceipt || !receiptInfo.isPO);

      const receiptNumber = validReceipt
        ? `CR-${receiptInfo.batchNumber ? `${receiptInfo.batchNumber}-` : ''}${receiptInfo.receiptNumber}`
        : '';

      return `Receipt Number ${receiptNumber}`;
    }

    const studentID = $ctrl.addressEntity?.entity?.studentID
      ? ` (${$ctrl.addressEntity.entity.studentID})`
      : '';
    const grade =
      studentID && $ctrl.addressEntity?.entity?.grade >= 0
        ? ` [${$ctrl.addressEntity.entity.grade}]`
        : '';

    return `${$ctrl.addressEntity.entity.name}${studentID}${grade}`;
  };
}

/*******************************************************************************
 * findEntityByDirection */
/**
 * Loads the next or previous entity (by name) into the dialog, reloading its
 * open orders and unpaid items.
 *
 * @param  next  `true`=Next entity, `false`=Previous entity.
 *******************************************************************************/
/* @ngInject */ function findEntityByDirection(
  BooksServices,
  Alert,
  BusyScreen,
  $q,
  $ctrl
) {
  return function (next) {
    BusyScreen.setBusy(true);

    return BooksServices.Entities.getEntityByDirection(
      $ctrl.addressEntity.entity.name,
      next
    )
      .then((newAddressEntity) => {
        if (!newAddressEntity) {
          Alert.info('No more entities to view.', 'Entity Navigation');
          BusyScreen.setBusy(false);
          return null;
        }

        /*------------------------------------------------------------*/
        /* Tear the child tree down. ng-if on contentReady removes    */
        /* info-actions / individual-info / receipt-history from the  */
        /* DOM, destroying their scopes and any internal grid state,  */
        /* selection, fetched receipt history, etc.                   */
        /*------------------------------------------------------------*/
        $ctrl.contentReady = false;

        /*------------------------------------------------------------*/
        /* Fetch the data in parallel. Receivables is short-circuited */
        /* to [] when showUnpaidItems is false (Booster/PTA case).    */
        /*------------------------------------------------------------*/
        return $q.all([
          $q.when(newAddressEntity),
          $ctrl.viewOpenOrdersHandler(null, newAddressEntity.entity.id, null),
          $ctrl.showUnpaidItems
            ? $ctrl.viewReceivablesItemsHandler(
                newAddressEntity.entity.id,
                null,
                null
              )
            : $q.when([])
        ]);
      })
      .then((results) => {
        if (!results) return;

        const [newAddressEntity, newOpenOrders, newReceivables] = results;

        /*------------------------------------------------------------*/
        /* Swap all parent-owned data atomically, then flip           */
        /* contentReady back on. Children remount on the next digest  */
        /* with the new bindings as their initial values, so $onInit  */
        /* sees fresh inputs from the start � no $onChanges dance.    */
        /*------------------------------------------------------------*/
        $ctrl.addressEntity = newAddressEntity;
        $ctrl.openOrders = newOpenOrders;
        $ctrl.receivables = newReceivables;
        $ctrl.contentReady = true;

        BusyScreen.setBusy(false);
      })
      .catch((resp) => {
        const display = next ? 'next' : 'previous';
        const error = resp?.parsedResponse
          ? resp.parsedResponse.message()
          : resp?.statusText || 'Unknown error';
        Alert.error(
          `Failed to retrieve the ${display} entity! ${error}`,
          'Entity Navigation'
        );
        /*------------------------------------------------------------*/
        /* Make sure we don't leave the modal stuck in the loading    */
        /* state if the request blew up after we tore the tree down. */
        /*------------------------------------------------------------*/
        $ctrl.contentReady = true;
        BusyScreen.setBusy(false);
      });
  };
}

/*******************************************************************************
 * viewOpenOrdersHandler */
/**
 * Retrieves a list of open orders for the entity
 *
 * This is used by both controllers (dialog & main).
 *******************************************************************************/
/* @ngInject */ function viewOpenOrdersHandler(
  BooksSvc,
  Alert,
  Tools,
  $ctrl,
  BarcodeValidator
) {
  return function () {
    const receiptInfo = BarcodeValidator.parse($ctrl.barcode || '');

    /**
     * The receipt will be invalid if it has decimals or if it is in purchase order format.
     */
    const validReceipt =
      receiptInfo.isValid && (receiptInfo.isReceipt || !receiptInfo.isPO);

    var promise = $ctrl.orgInfo
      ? BooksSvc.viewOpenOrders()
      : BooksSvc.viewOpenOrders(
          validReceipt ? Number(receiptInfo?.batchNumber) : null,
          validReceipt ? Number(receiptInfo?.receiptNumber) : null,
          $ctrl.addressEntity?.entity?.id,
          null
        );

    return promise
      .then((resp) => resp.data)
      .catch((resp) => {
        const error = Tools.serviceErrorHandler(resp);

        Alert.error(
          `
          Failed to refresh the list of open orders 
          Error: ${error}
        `,
          'Open Orders'
        );
      });
  };
}

/*******************************************************************************
 * viewReceivablesItemsHandler */
/**
 * Retrieves a list of unpaid items for the entity.
 *
 * This is used by both controllers (dialog & main).
 *******************************************************************************/
/* @ngInject */ function viewReceivablesItemsHandler(
  BooksServices,
  Alert,
  Tools,
  SERVER,
  $ctrl,
  BarcodeValidator
) {
  return function () {
    const receiptInfo = BarcodeValidator.parse($ctrl.barcode || '');

    /**
     * The receipt will be invalid if it has decimals or if it is in purchase order format.
     */
    const validReceipt =
      receiptInfo.isValid && (receiptInfo.isReceipt || !receiptInfo.isPO);

    var promise = $ctrl.orgInfo
      ? BooksServices.Receipts.viewReceivablesItems(null, null, null)
      : BooksServices.Receipts.viewReceivablesItems(
          $ctrl.addressEntity?.entity?.id,
          validReceipt ? Number(receiptInfo?.receiptNumber) : null,
          validReceipt ? Number(receiptInfo?.batchNumber) : null
        );

    return promise
      .then((resp) => {
        /*--------------------------------------------------------------*/
        /* ASBWorks' /viewReceivablesItems returns                     */
        /* { receivables, hasDestiny, destinyLastRefreshedUTC,          */
        /* destinyNextUpdateUTC, destinyLastFailedUTC, destinyLastError, */
        /* destinyRefreshInProgress } so the dialog can show Destiny    */
        /* cache freshness - including a refresh that failed after the  */
        /* user stopped waiting for it - next to the fines it displays. */
        /* Other products (Booster, PTA) don't have Destiny and still   */
        /* resolve a bare array - handle both shapes here rather than   */
        /* push the branching into every caller.                       */
        /*--------------------------------------------------------------*/
        const isWrapped = !Array.isArray(resp);
        const receivables = isWrapped ? resp.receivables : resp;

        $ctrl.destinyStatus = isWrapped
          ? {
              hasDestiny: resp.hasDestiny,
              lastRefreshedUTC: resp.destinyLastRefreshedUTC,
              nextUpdateUTC: resp.destinyNextUpdateUTC,
              lastFailedUTC: resp.destinyLastFailedUTC,
              lastError: resp.destinyLastError,
              refreshInProgress: resp.destinyRefreshInProgress
            }
          : { hasDestiny: false };

        return receivables.map((receivable) => {
          const remainingBalance = Tools.roundMoney(
            receivable.outstanding - receivable.received
          );

          receivable.balance = remainingBalance;
          receivable.amountToPay = remainingBalance;

          Object.defineProperty(receivable, 'forFine', {
            get: function () {
              return receivable.subType === SERVER.BooksDBConstants.LIS_FINE;
            }
          });

          Object.defineProperty(receivable, 'forItem', {
            get: function () {
              return (
                receivable.subType ===
                SERVER.BooksDBConstants.LIS_RECEIVABLE_ITEM_CHARGE
              );
            }
          });

          return receivable;
        });
      })
      .catch((resp) => {
        const error = Tools.serviceErrorHandler(resp);

        Alert.error(
          `
          Failed to refresh the list of unpaid items.
          Error: ${error}
        `,
          'Unpaid Items'
        );
      });
  };
}

/*******************************************************************************
 * showManualRefreshResult */
/**
 * Builds the alert shown after a manual Destiny refresh attempt - shared by
 * refreshDestinyFinesHandler (site-wide) and refreshDestinyEntityFinesHandler
 * (per-entity) since the server returns the same ManualRefreshResult shape ({
 * started, retryAfterUTC, error }) for both.
 *
 * A cooldown (retryAfterUTC set) is shown as a real error with the actual time
 * the button will work again, rather than a vague "try again later" - the user
 * asked specifically for this so people understand *why* the button didn't do
 * anything, not just that it didn't.
 *
 * The server waits up to 30 seconds for the refresh to finish before answering,
 * so `completed` means it's actually done (and `error`, if set alongside it,
 * says why it failed - the cached fines were left unchanged). Only a refresh
 * still running after that gets the "takes a few minutes" startedMessage - if it
 * then fails in the background, the server records it and the Destiny status
 * shows "Last refresh failed" (with the reason) the next time Unpaid Items load.
 * A failed refresh never starts the cooldown, so the user can retry right away.
 *
 * @param  Alert  Alert service.
 * @param  data  { started, completed, retryAfterUTC, error } response.
 * @param  startedMessage  Shown when the refresh is still running.
 * @param  completedMessage  Shown when the refresh finished successfully.
 *******************************************************************************/
function showManualRefreshResult(
  Alert,
  data,
  startedMessage,
  completedMessage
) {
  if (data.error) {
    Alert.error(data.error, 'Destiny Fines');
    return;
  }

  if (data.completed) {
    Alert.success(completedMessage, 'Destiny Fines');
    return;
  }

  if (data.started) {
    Alert.info(startedMessage, 'Destiny Fines');
    return;
  }

  if (data.retryAfterUTC) {
    const retryAt = moment.utc(data.retryAfterUTC).local();

    Alert.error(
      `This was already refreshed recently. You can try again at ${retryAt.format('h:mm A')} (${retryAt.fromNow()}).`,
      'Destiny Fines'
    );
    return;
  }

  Alert.error(
    'Unable to start a Destiny fines refresh right now.',
    'Destiny Fines'
  );
}

/*******************************************************************************
 * refreshDestinyFinesHandler */
/**
 * Kicks off a manual Destiny fines refresh for the site-wide "Refresh" button on
 * the receivables info dialog's Destiny accordion (ASBWorks only - other
 * products get `started: false` back from the no-op base route and just never
 * show the button, since $ctrl.destinyStatus.hasDestiny is never true for them).
 * Fire-and-forget: the real refresh runs on a detached background thread
 * server-side and takes a few minutes, so this just starts it and tells the user
 * so rather than waiting on it.
 *
 * This is used by the modal controller only (not the dialog service) - it's only
 * reachable once the dialog is already open.
 *******************************************************************************/
/* @ngInject */ function refreshDestinyFinesHandler(
  BooksServices,
  Alert,
  Tools
) {
  return function () {
    return BooksServices.Receipts.refreshDestinyFines()
      .then((data) => {
        showManualRefreshResult(
          Alert,
          data,
          'Destiny fines refresh is still running - this can take a few minutes for a large site. Reopen this dialog afterward to see the updated fines, or why the refresh failed.',
          'Destiny fines have been refreshed for the site.'
        );

        return data;
      })
      .catch((resp) => {
        const error = Tools.serviceErrorHandler(resp);

        Alert.error(
          `
          Failed to start the Destiny fines refresh.
          Error: ${error}
        `,
          'Destiny Fines'
        );
      });
  };
}

/*******************************************************************************
 * refreshDestinyEntityFinesHandler */
/**
 * Same idea as refreshDestinyFinesHandler, but scoped to just the currently
 * selected entity's fines - backs the per-entity "Refresh <student>" button on
 * the Destiny accordion. Only reachable when an entity is actually selected (see
 * infoActions.template.html), so $ctrl.addressEntity.entity.id is always set
 * here.
 *
 * This is used by the modal controller only (not the dialog service) - it's only
 * reachable once the dialog is already open.
 *******************************************************************************/
/* @ngInject */ function refreshDestinyEntityFinesHandler(
  BooksServices,
  Alert,
  Tools,
  $ctrl
) {
  return function () {
    return BooksServices.Receipts.refreshDestinyEntityFines(
      $ctrl.addressEntity.entity.id
    )
      .then((data) => {
        showManualRefreshResult(
          Alert,
          data,
          `Destiny fines refresh for ${$ctrl.addressEntity.entity.name} is still running - this can take a few minutes. Reopen this dialog afterward to see the updated fines, or why the refresh failed.`,
          `Destiny fines have been refreshed for ${$ctrl.addressEntity.entity.name}.`
        );

        return data;
      })
      .catch((resp) => {
        const error = Tools.serviceErrorHandler(resp);

        Alert.error(
          `
          Failed to start the Destiny fines refresh.
          Error: ${error}
        `,
          'Destiny Fines'
        );
      });
  };
}
